import { Trans } from '@lingui/react/macro';
import { Settings } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DEFAULT_ADVANCED_SETTINGS } from '@/domain/templates/defaults';
import type { AdvancedSettings } from '@/types/template';

import { RulesForm } from './rules-form';

/**
 * Header affordance for the template's scheduling rules — a popover wrapping
 * {@link RulesForm}, replacing the wizard's dedicated "Advanced" step. The
 * trigger shows a small count of how many rules have been customised away from
 * their defaults.
 */
export function RulesPopover({ advancedSettings, onChange, triggerClassName }: RulesPopoverProps) {
  const customCount = countCustomRules(advancedSettings);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className={triggerClassName}>
          <Settings className="size-3" />
          <Trans>Scheduling rules</Trans>
          {customCount > 0 && (
            <span
              data-testid="rules-custom-count"
              className="ml-1 inline-flex min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[length:var(--tiny-font-size)] leading-[var(--tiny-line-height)] font-semibold text-primary-foreground"
            >
              {customCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      {/* Anchored to the trigger's left edge so the popover doesn't shift when
          the count badge changes the trigger's width. */}
      <PopoverContent align="start" className="w-80">
        <RulesForm advancedSettings={advancedSettings} onChange={onChange} />
      </PopoverContent>
    </Popover>
  );
}

interface RulesPopoverProps {
  advancedSettings: AdvancedSettings;
  onChange: (settings: AdvancedSettings) => void;
  triggerClassName?: string;
}

/**
 * The rules the badge counts. `maxSplitShiftsPerDay` is intentionally excluded —
 * it's a detail of the split-shifts rule, which `allowSplitShifts` already
 * represents.
 */
const COUNTED_RULE_KEYS = [
  'overrideRestBreak',
  'allowSplitShifts',
  'minDaysOffPerWeek',
  'consecutiveDaysOff',
  'maxConsecutiveDaysWorked',
] as const;

/**
 * Number of scheduling rules whose value differs from its default. Diffs against
 * the canonical {@link DEFAULT_ADVANCED_SETTINGS}; an absent value means "use the
 * default", so it doesn't count as a customisation.
 */
function countCustomRules(settings: AdvancedSettings): number {
  return COUNTED_RULE_KEYS.filter(
    (key) => (settings[key] ?? DEFAULT_ADVANCED_SETTINGS[key]) !== DEFAULT_ADVANCED_SETTINGS[key],
  ).length;
}
